import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@brent/core-ui/ui'
import { Button } from '@brent/core-ui/ui'
import { Pencil } from 'lucide-react'
import { SuggestionForm } from '@brent/core-ui/components'
import type { SuggestionFormData } from '@brent/shared/validation'
import { useUpdateUserSuggestion } from '@/hooks/useUserSuggestions'
import { useToast } from '@/hooks/use-toast'
import { searchComposers, getComposerWorks } from '@/services/api/wikidata-lambda'
import type { Suggestion } from '@brent/shared/types'

interface EditUserSuggestionDialogProps {
  username: string
  suggestion: Suggestion
  memberName: string
}

export const EditUserSuggestionDialog = ({
  username,
  suggestion,
  memberName,
}: EditUserSuggestionDialogProps) => {
  const [open, setOpen] = useState(false)
  const { toast } = useToast()
  const updateMutation = useUpdateUserSuggestion(username)

  const handleSubmit = async (data: SuggestionFormData) => {
    try {
      await updateMutation.mutateAsync({
        suggestionId: suggestion.suggestion_id,
        data,
      })
      toast({
        title: 'Suggestion updated',
        description: `Updated suggestion for ${memberName}`,
      })
      setOpen(false)
    } catch (error) {
      toast({
        title: 'Failed to update suggestion',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Suggestion for {memberName}</DialogTitle>
          <DialogDescription>Update this piece suggestion.</DialogDescription>
        </DialogHeader>
        <SuggestionForm
          defaultValues={{
            composer: suggestion.composer,
            title: suggestion.title,
            composer_wikidata_id: suggestion.composer_wikidata_id || null,
            work_wikidata_id: suggestion.work_wikidata_id || null,
            comment: suggestion.comment || '',
          }}
          onSubmit={handleSubmit}
          onCancel={() => setOpen(false)}
          isLoading={updateMutation.isPending}
          submitLabel="Update Suggestion"
          commentPlaceholder="Why should the orchestra play this piece?"
          searchComposers={searchComposers}
          searchWorks={getComposerWorks}
        />
      </DialogContent>
    </Dialog>
  )
}
